// src/components/ContactWidget/ContactWidget.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  FaComments, 
  FaTimes, 
  FaMinus,
  FaPhone,
  FaWhatsapp,
  FaEnvelope,
  FaMapMarkerAlt 
} from 'react-icons/fa';
import { trackBusinessEvents } from '../../utils/analytics';
import styles from './ContactWidget.module.css';

const ContactWidget = () => {
  const { t } = useTranslation(['common']);

  const [widgetState, setWidgetState] = useState('collapsed');
  const [isShaking, setIsShaking] = useState(false);
  const [isPulsing, setIsPulsing] = useState(false);
  const widgetRef = useRef(null);
  
  // Contact information
  const contactInfo = {
    phone: '013-222 8584',
    phoneDisplay: '013-222 8584',
    whatsapp: '60132228584',
    email: 'info@adtac.co',
    name: 'Dalila'
  };

  // Track widget view when component mounts
  useEffect(() => {
    trackBusinessEvents.menuNavigation('contact_widget_view', 'floating_widget');
  }, []);

  // Auto-shake animation every 5 seconds when collapsed
  useEffect(() => {
    if (widgetState === 'collapsed') {
      const shakeInterval = setInterval(() => {
        setIsPulsing(true);
        setTimeout(() => setIsPulsing(false), 2000);
      }, 3000);

      return () => clearInterval(shakeInterval);
    }
  }, [widgetState]);

  // Pulse animation every 8 seconds when minimized
  useEffect(() => {
    if (widgetState === 'minimized') {
      const pulseInterval = setInterval(() => {
        setIsPulsing(true);
        setTimeout(() => setIsPulsing(false), 2000);
      }, 8000);

      return () => clearInterval(pulseInterval);
    }
  }, [widgetState]);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (widgetRef.current && !widgetRef.current.contains(event.target) && widgetState === 'expanded') {
        setWidgetState('collapsed');
        trackBusinessEvents.menuNavigation('widget_close_outside', 'contact_widget');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [widgetState]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && widgetState === 'expanded') {
        setWidgetState('collapsed');
        trackBusinessEvents.menuNavigation('widget_close_escape', 'contact_widget');
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [widgetState]);

  const handleButtonClick = () => {
    if (widgetState === 'collapsed' || widgetState === 'minimized') {
      setWidgetState('expanded');
      trackBusinessEvents.menuNavigation('widget_open', 'contact_widget');
    }
  };

  const handleClose = () => {
    setWidgetState('collapsed');
    trackBusinessEvents.menuNavigation('widget_close', 'contact_widget');
  };

  const handleMinimize = () => {
    setWidgetState('minimized');
    trackBusinessEvents.menuNavigation('widget_minimize', 'contact_widget');
  };

  const handlePhoneCall = () => {
    trackBusinessEvents.phoneClick(contactInfo.phone, 'contact_widget');
    trackBusinessEvents.leadGeneration('contact_widget', 'phone_call');
    window.location.href = `tel:${contactInfo.phone}`;
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(
      t('contactWidget.whatsappMessage', { name: contactInfo.name })
    );
    trackBusinessEvents.whatsappClick('contact_widget');
    trackBusinessEvents.leadGeneration('contact_widget', 'whatsapp_chat');
    window.open(`https://wa.me/${contactInfo.whatsapp}?text=${message}`, '_blank');
  };

  const handleEmail = () => {
    const subject = encodeURIComponent(t('contactWidget.emailSubject'));
    const body = encodeURIComponent(
      t('contactWidget.emailBody', { name: contactInfo.name })
    );
    trackBusinessEvents.emailClick(contactInfo.email, 'contact_widget');
    trackBusinessEvents.leadGeneration('contact_widget', 'email_inquiry');
    window.location.href = `mailto:${contactInfo.email}?subject=${subject}&body=${body}`;
  };

  const handleLocation = () => {
    trackBusinessEvents.menuNavigation('visit_office', 'contact_widget');
    window.open('/contact', '_blank');
  };

  const getButtonClasses = () => {
    let classes = [styles.contactButton];
    
    if (widgetState === 'minimized') {
      classes.push(styles.minimized);
    }
    
    if (isShaking) {
      classes.push(styles.shaking);
    }
    
    if (isPulsing) {
      classes.push(styles.pulsing);
    }
    
    return classes.join(' ');
  };

  return (
    <div className={styles.contactWidget} ref={widgetRef}>
      {widgetState !== 'expanded' && (
        <button
          className={getButtonClasses()}
          onClick={handleButtonClick}
          aria-label={t('contactWidget.openLabel')}
          title={t('contactWidget.connectTitle')}
        >
          <FaComments />
        </button>
      )}

      {widgetState === 'expanded' && (
        <div className={styles.contactPanel}>
          <div className={styles.panelHeader}>
            <h3 className={styles.panelTitle}>
              {t('contactWidget.panelTitle')}
            </h3>
            <div>
              <button
                className={styles.minimizeButton}
                onClick={handleMinimize}
                aria-label={t('contactWidget.minimizeLabel')}
                title={t('contactWidget.minimize')}
              >
                <FaMinus />
              </button>
              <button
                className={styles.closeButton}
                onClick={handleClose}
                aria-label={t('contactWidget.closeLabel')}
                title={t('contactWidget.close')}
              >
                <FaTimes />
              </button>
            </div>
          </div>
          
          <div className={styles.panelContent}>
            <button
              className={styles.contactOption}
              onClick={handlePhoneCall}
              title={t('contactWidget.callTitle', { name: contactInfo.name })}
            >
              <FaPhone className={styles.contactIcon} />
              <div className={styles.contactInfo}>
                <div className={styles.contactLabel}>
                  {t('contactWidget.callLabel', { name: contactInfo.name })}
                </div>
                <p className={styles.contactDetails}>{contactInfo.phoneDisplay}</p>
              </div>
            </button>

            <button
              className={styles.contactOption}
              onClick={handleWhatsApp}
              title={t('contactWidget.whatsappTitle')}
            >
              <FaWhatsapp className={styles.contactIcon} />
              <div className={styles.contactInfo}>
                <div className={styles.contactLabel}>
                  {t('contactWidget.whatsappLabel')}
                </div>
                <p className={styles.contactDetails}>
                  {t('contactWidget.quickResponse')}
                </p>
              </div>
            </button>

            <button
              className={styles.contactOption}
              onClick={handleEmail}
              title={t('contactWidget.emailTitle')}
            >
              <FaEnvelope className={styles.contactIcon} />
              <div className={styles.contactInfo}>
                <div className={styles.contactLabel}>
                  {t('contactWidget.emailLabel')}
                </div>
                <p className={styles.contactDetails}>{contactInfo.email}</p>
              </div>
            </button>

            <button
              className={styles.contactOption}
              onClick={handleLocation}
              title={t('contactWidget.visitTitle')}
            >
              <FaMapMarkerAlt className={styles.contactIcon} />
              <div className={styles.contactInfo}>
                <div className={styles.contactLabel}>
                  {t('contactWidget.visitLabel')}
                </div>
                <p className={styles.contactDetails}>
                  {t('contactWidget.location')}
                </p>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactWidget;